export interface MentionToken {
  type: 'text' | 'mention'
  value: string
}

export interface MentionTarget {
  name: string
}

/**
 * Quebra `text` em tokens, marcando como `mention` apenas as ocorrências de
 * `@<name>` que correspondem EXATAMENTE a um nome da lista de candidatos.
 *
 * Espelha o `parseMentions` do backend (longest-first, palavra-inteira) para
 * que a UI mostre em azul exatamente o mesmo conjunto que o servidor vai
 * notificar — sem falsos positivos.
 */
export function splitMentionTokens(
  text: string,
  candidates: MentionTarget[],
): MentionToken[] {
  if (!text) return []
  if (!candidates || candidates.length === 0) {
    return [{ type: 'text', value: text }]
  }

  // Acha intervalos [start, end) das menções, ordenando longest-first para
  // priorizar "@João Silva Santos" em vez do prefixo "@João".
  const sorted = [...candidates].sort((a, b) => b.name.length - a.name.length)
  const ranges: Array<{ start: number; end: number }> = []
  const claimed = new Array<boolean>(text.length).fill(false)

  for (const c of sorted) {
    const token = `@${c.name}`
    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    // Mesma regra do backend: depois do nome, fim de string OU caractere
    // não-palavra (espaço, pontuação, etc.).
    const regex = new RegExp(`${escaped}(?=$|[^\\p{L}\\p{N}])`, 'gu')
    let match: RegExpExecArray | null
    while ((match = regex.exec(text)) !== null) {
      const start = match.index
      const end = start + match[0].length
      // Evita sobreposição com mention já marcada (longest-first protege).
      let conflict = false
      for (let i = start; i < end; i++) {
        if (claimed[i]) {
          conflict = true
          break
        }
      }
      if (conflict) continue
      for (let i = start; i < end; i++) claimed[i] = true
      ranges.push({ start, end })
    }
  }

  if (ranges.length === 0) return [{ type: 'text', value: text }]

  ranges.sort((a, b) => a.start - b.start)
  const out: MentionToken[] = []
  let cursor = 0
  for (const r of ranges) {
    if (r.start > cursor) {
      out.push({ type: 'text', value: text.slice(cursor, r.start) })
    }
    out.push({ type: 'mention', value: text.slice(r.start, r.end) })
    cursor = r.end
  }
  if (cursor < text.length) {
    out.push({ type: 'text', value: text.slice(cursor) })
  }
  return out
}
